import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { Event } from "event-system";
import { gsap } from "gsap";
import { Container, FederatedPointerEvent, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { Avatar } from "../../../common/components/Avatar";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { HBox } from "../../../common/components/ui/HBox";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { LandlordGlowRotationAnimation } from "../../buildingRankUp/components/LandlordGlowRotationAnimation";
import { ShineRenterAnimation } from "../../buildingRankUp/components/ShineRenterAnimation";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { BuildingUpgradeItemResourcePackage } from "../resources/BuildingUpgradeItemResourcePackage";
import { RenterPiggyClubContainer } from "./RenterPiggyClubContainer";
import { UpgradeResult } from "../../../common/models/Building/upgrade/UpgradeResult";
import { FixResult } from "../../../common/models/Building/upgrade/FixResult";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { PiggyMultiplier } from "../../../common/components/PiggyMultiplier";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";

export class BuildingUpgradeItemRenterRewardComponent extends Container {
    private static readonly YOU_WON_TEXT = 'YOU WON';

    private landlordIcon: Sprite;
    private landlordAvatar: Avatar;
    private landlordInfoButton: ButtonWithScaleAnimation;
    private landlordRewardBox: HBox;
    private landlordRewardCount: Text;
    private landlordRewardIcon: Sprite;
    private landlordGlowAnimation: LandlordGlowRotationAnimation;
    private landlordUnderlineGlow: Sprite;
    private landlordPiggyIcon: Sprite;
    private landlordPiggyIncludedText: Text;

    private greenLight: Sprite;

    private youWonText: Text;
    private renterAvatar: Avatar;
    private renterInfoButton: ButtonWithScaleAnimation;
    private renterRewardBox: HBox;
    private renterRewardCount: Text;
    private renterRewardIcon: Sprite;
    private shineRenterAnimation: ShineRenterAnimation;
    private renterPiggyClubContainer: RenterPiggyClubContainer;

    public get onRenterInfoButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.renterInfoButton.onUp;
    }
    public get onLandlordInfoButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.landlordInfoButton.onUp;
    }

    public constructor(
        commonResources: CommonResourcesPackage,
        upgradeItemResources: BuildingUpgradeItemResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        renterAvatarTexture: Texture,
        landlordAvatarTexture: Texture,
        upgradeOrFixResult: FixResult | UpgradeResult,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler
    ) {
        super();

        this.landlordIcon = new Sprite(upgradeItemResources.assets_views_building_upgrade_item_landlord_icon_png);
        this.landlordInfoButton = new ButtonWithScaleAnimation(upgradeItemResources.assets_views_building_upgrade_item_info_icon_png);
        this.landlordAvatar = new Avatar(userProfileResources, landlordAvatarTexture);
        this.landlordAvatar.avatarSize = new Point(180, 181);
        this.landlordAvatar.glow = true;
        this.landlordAvatar.enabled = false;
        this.landlordRewardBox = new HBox(15);
        this.landlordRewardCount = new Text(upgradeOrFixResult.landlordRewardInTokens, new RewardLandlordTextStyle());
        this.landlordRewardIcon = new Sprite(upgradeItemResources.assets_views_building_upgrade_item_token_png);
        this.landlordRewardIcon.width = this.landlordRewardIcon.height = 73;
        this.landlordRewardBox.addChild(this.landlordRewardCount, this.landlordRewardIcon);
        this.landlordGlowAnimation = new LandlordGlowRotationAnimation(canvasScaler);
        this.landlordUnderlineGlow = new Sprite(upgradeItemResources.assets_views_building_upgrade_item_green_light_png);
        this.landlordUnderlineGlow.width = 165;
        this.landlordUnderlineGlow.height = 10;
        this.landlordPiggyIcon = new PiggyMultiplier(commonResources);
        this.landlordPiggyIcon.width = 78;
        this.landlordPiggyIcon.height = 68;
        this.landlordPiggyIncludedText = new Text(`INCLUDED`, new RewardLandlordTextStyle());
        this.landlordPiggyIncludedText.style.fontSize = 36;
        this.landlordPiggyIncludedText.style.letterSpacing = 1.4;

        this.greenLight = new Sprite(upgradeItemResources.assets_views_building_upgrade_item_green_light_png);

        this.youWonText = new Text(BuildingUpgradeItemRenterRewardComponent.YOU_WON_TEXT, new YouWonTextStyle());
        this.renterAvatar = new Avatar(userProfileResources, renterAvatarTexture);
        this.renterAvatar.avatarSize = new Point(120, 121);
        this.renterAvatar.glow = playerModel.isAuthorized;
        this.renterAvatar.enabled = false;
        this.renterInfoButton = new ButtonWithScaleAnimation(upgradeItemResources.assets_views_building_upgrade_item_info_icon_png);
        this.renterRewardBox = new HBox(10);

        let formattedTokenAmount: string;
        const tokenAmount = parseFloat(upgradeOrFixResult.currentGuestRewardInTokens);
        if (Number.isInteger(tokenAmount)) {
            formattedTokenAmount = StringFormatExtention.formatWithComma(tokenAmount.toString());
        } else {
            const fixedTokenAmount = tokenAmount.toFixed(4);
            formattedTokenAmount = StringFormatExtention.formatWithComma(parseFloat(fixedTokenAmount).toString());
        }
        this.renterRewardCount = new Text(formattedTokenAmount, new RewardRenterTextStyle());
        this.renterRewardIcon = new Sprite(upgradeItemResources.assets_views_building_upgrade_item_token_png);
        this.renterRewardIcon.width = this.renterRewardIcon.height = 49;
        this.renterRewardBox.addChild(this.renterRewardCount, this.renterRewardIcon);
        this.shineRenterAnimation = new ShineRenterAnimation(canvasScaler);
        this.shineRenterAnimation.visible = playerModel.isAuthorized;

        this.addChild(
            this.landlordIcon,
            this.landlordAvatar,
            this.landlordGlowAnimation,
            this.landlordInfoButton,
            this.landlordRewardBox,
            this.landlordUnderlineGlow,
            this.landlordPiggyIcon,
            this.landlordPiggyIncludedText,
            this.greenLight,
            this.youWonText,
            this.renterAvatar,
            this.shineRenterAnimation,
            this.renterInfoButton,
            this.renterRewardBox
        );
        //TODO: Condition for displaying
        if (upgradeOrFixResult.piggyClubMultiplier != 0) {
            this.renterPiggyClubContainer = new RenterPiggyClubContainer(upgradeItemResources, upgradeOrFixResult);
            this.renterPiggyClubContainer.position.set(270, 269);
            this.addChild(this.renterPiggyClubContainer);
            this.landlordUnderlineGlow.visible = false;
            this.landlordPiggyIcon.visible = false;
            this.landlordPiggyIncludedText.visible = false;
        }
        this.landlordInfoButton.visible = false;
        this.renterInfoButton.visible = false;

        this.layout();
    }

    private layout(): void {
        this.landlordIcon.position.set(6, 13);
        this.landlordInfoButton.position.set(-28, 83);
        this.landlordAvatar.position.set(0, 84);
        this.landlordRewardBox.position.set(this.landlordAvatar.position.x + this.landlordAvatar.width * 0.5 - this.landlordRewardBox.width * 0.5, 289);
        this.landlordRewardIcon.position.y = -14;
        this.landlordGlowAnimation.position.set(this.landlordAvatar.x + this.landlordAvatar.width * 0.5 - 3, -15);
        this.landlordUnderlineGlow.position.set(0, 351);
        this.landlordPiggyIcon.position.set(-14, 359);
        this.landlordPiggyIncludedText.position.set(77, 383);
        this.greenLight.position.set(209, 0);
        this.youWonText.position.set(264, 8);
        this.renterInfoButton.position.set(253, 83);
        this.renterAvatar.position.set(289, 83);
        this.shineRenterAnimation.scale.set(1.48, 0.32);
        this.shineRenterAnimation.position.set(this.renterAvatar.x + this.renterAvatar.width * 0.5 - 2, this.renterAvatar.y + this.renterAvatar.height * 0.5 - 3);
        this.renterRewardBox.position.set(this.renterAvatar.position.x + this.renterAvatar.width * 0.5 - this.renterRewardBox.width * 0.5, 224);
        this.renterRewardIcon.position.y = -10;
    }

    public async playAvatarsAnimations(): Promise<void> {
        await this.landlordGlowAnimation.play();
        await this.shineRenterAnimation.play();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.delay(1);
        animationTimeLine.fromTo(this.landlordInfoButton, { alpha: 0 }, {
            alpha: 1, duration: 0.5, onStart: () => {
                this.landlordInfoButton.visible = true;
                this.renterInfoButton.visible = true;
            }
        });
        animationTimeLine.fromTo(this.renterInfoButton, { alpha: 0 }, { alpha: 1, duration: 0.5 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    public hide(): void {
        this.landlordGlowAnimation.visible = false;
        this.shineRenterAnimation.visible = false;
    }
}

class YouWonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            fill: '#FFFFFF'
        })
    }
}

class RewardRenterTextStyle extends YouWonTextStyle {
    public constructor() {
        super();
        this.fontSize = 36;
    }
}

class RewardLandlordTextStyle extends YouWonTextStyle {
    public constructor() {
        super();
        this.fontSize = 57;
    }
}